/**
 * Variables (Types) Data Source
 */

import type { 
  DataSourceModule, 
  FieldMetadata, 
  ColumnDefinition, 
  ListWidgetItem,
  TableDataOutput,
  TableItemsOutput,
  TableDataRow,
  DataSourceConfig,
  TransformerContext
} from '../types';
import { TypeItem, getCachedTypes } from "@/lib/stores/typesStore";
import { getFolderPath } from "@/lib/stores/folderStore";
import { formatDate } from '../shared/formatters';
import { CardData } from '@/components/layout/element-layout/grid-layout/GridLayout';
import { formatRelativeTime } from "@/lib/utils";

// Field definitions with metadata
const fields: Record<string, FieldMetadata> = {
  id: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  name: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: true,
    required: true,
    validation: [
      { type: 'required', message: 'Name is required' }
    ]
  },
  element_type: {
    type: 'string',
    editable: 'dropdown',
    filterable: true,
    sortable: true,
    options: [
      { label: 'String', value: 'string' },
      { label: 'Number', value: 'number' },
      { label: 'Percentage', value: 'percentage' },
      { label: 'Range', value: 'range' },
      { label: 'Type', value: 'type' },
      { label: 'Boolean', value: 'boolean' },
    ]
  },
  description: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: false,
  },
  group_name: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: true,
  },
  folder: {
    type: 'string',
    editable: 'dropdown',
    filterable: true,
    sortable: true,
    linkedDataSource: 'folders',
    folderItemType: 'library', // Variables use the same folder structure as library
    displayField: 'name',
    valueField: 'id',
  },
  data: {
    type: 'array',
    editable: false,
    filterable: false,
    sortable: false,
    formatter: (val) => {
      if (!Array.isArray(val)) return '0 values';
      const count = val.length;
      return `${count} ${count === 1 ? 'value' : 'values'}`;
    }
  },
  created_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  },
  updated_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  }
};

// Column definitions
const columns: ColumnDefinition[] = [
  { 
    key: 'id', 
    label: 'ID', 
    filterable: true, 
    sortable: true,
    metadata: fields.id 
  },
  { 
    key: 'name', 
    label: 'Name', 
    filterable: true, 
    sortable: true,
    editable: 'text',
    metadata: fields.name 
  },
  { 
    key: 'element_type', 
    label: 'Type', 
    filterable: true, 
    sortable: true,
    editable: 'dropdown',
    metadata: fields.element_type 
  },
  { 
    key: 'description', 
    label: 'Description', 
    filterable: true,
    editable: 'text',
    metadata: fields.description 
  },
  { 
    key: 'group_name', 
    label: 'Group', 
    filterable: true,
    sortable: true,
    editable: 'text',
    metadata: fields.group_name 
  },
  { 
    key: 'folder', 
    label: 'Folder', 
    filterable: true,
    metadata: fields.folder 
  },
  { 
    key: 'data', 
    label: 'Values', 
    metadata: fields.data 
  },
  { 
    key: 'created_at', 
    label: 'Created', 
    align: 'left',
    sortable: true,
    metadata: fields.created_at 
  },
  { 
    key: 'updated_at', 
    label: 'Updated', 
    align: 'left',
    sortable: true,
    metadata: fields.updated_at 
  }
];

// Configuration
const config: DataSourceConfig = {
  type: 'objects', // Using 'objects' type since variables is not in the enum
  title: 'Variables',
  description: 'Variables and types',
  expandable: false,
  icon: 'variable'
};

// Data fetcher
function fetchData(selectedGame?: any, selectedFolderId?: string | null): TypeItem[] {
  if (!selectedGame?.id) return [];
  
  // Get base data from cache
  const baseData = getCachedTypes(selectedGame.id);
  
  // If folder filter is specified, filter by folder
  if (selectedFolderId !== undefined && selectedFolderId !== null) {
    return baseData.filter(item => item.folder === selectedFolderId);
  }
  
  return baseData;
}

// Transformers
function toList(rawData: TypeItem[]): ListWidgetItem[] {
  return rawData.map(item => ({
    id: item.id,
    name: item.name,
    tags: '', // Variables don't have tags
    type: item.element_type || 'string',
    created_at: item.created_at,
    data: { 
      elementCount: item.data?.length || 0,
      elementType: item.element_type
    },
    updated_at: item.updated_at,
    description: item.description || `${item.data?.length || 0} values`
  }));
}

function toTable(rawData: TypeItem[]): TableDataOutput {
  return {
    columns,
    data: rawData.map(item => ({
      id: item.id,
      name: String(item.name || ''),
      element_type: String(item.element_type || 'string'),
      description: String(item.description || ''),
      group_name: String(item.group_name || ''),
      folder: getFolderPath(item.game_id, 'variables', item.folder) || '',
      data: fields.data.formatter ? fields.data.formatter(item.data) : String(item.data?.length || 0),
      created_at: formatDate(item.created_at),
      updated_at: formatDate(item.updated_at),
    })),
    metadata: {
      totalCount: rawData.length,
      filteredCount: rawData.length
    }
  };
}

/**
 * Get display label for element type
 */
function getElementTypeLabel(elementType: string): string {
  const typeMap: Record<string, string> = {
    'string': 'String',
    'number': 'Number',
    'percentage': 'Percentage',
    'range': 'Range',
    'type': 'Type',
    'boolean': 'Boolean',
  };
  return typeMap[elementType] || elementType;
}

/**
 * Transform raw data to CardData format
 * @param rawData - Array of TypeItem from store
 * @param context - Optional context for resolving IDs
 */
function toCard(rawData: TypeItem[], context?: TransformerContext): CardData[] {
  return rawData.map(item => {
    const elementCount = item.data?.length || 0;
    const elementCountText = `${elementCount} ${elementCount === 1 ? 'value' : 'values'}`;
    
    const cardData: CardData = {
      id: item.id,
      gameId: item.game_id,
      title: item.name || '',
      owner: '', // Variables don't have owners
      ownerId: undefined,
      editPermissions: 'Open',
      creator: "System",
      tags: [], // Variables don't have tags
      description: item.description || elementCountText,
      lastUpdate: formatRelativeTime(item.updated_at),
      version: undefined, // Variables don't have versions
      icon: 'variable',
      shadowType: "black" as const,
      shadowColor: undefined,
      folder: item.folder,
      itemType: 'variables',
      // Custom data for variables
      elementType: item.element_type,
      elementTypeLabel: getElementTypeLabel(item.element_type),
      elementCount: elementCount,
      groupName: item.group_name,
    };
    return cardData;
  });
}

/**
 * Column keys for variables table view
 */
const TABLE_COLUMNS = [
  "Name", "Type", "Group", "Values", "Folder", "Description", "Last Update"
];

/**
 * Transform raw data to TableItem[] format for MainTableWidget
 * @param rawData - Array of TypeItem from store
 * @param context - Optional context
 */
function toTableItems(rawData: TypeItem[], context?: TransformerContext): TableItemsOutput {
  const items: TableDataRow[] = rawData.map(item => {
    const elementCount = item.data?.length || 0;
    const elementCountText = `${elementCount} ${elementCount === 1 ? 'value' : 'values'}`;
    
    return {
      type: "row" as const,
      key: item.id,
      depth: 0,
      isLocked: false,
      editPermissions: 'Open' as const,
      content: TABLE_COLUMNS.map((columnKey, index) => {
        const normalizedKey = columnKey.toLowerCase().replace(/\s+/g, "");
        let content: string | boolean | number = "";

        switch (normalizedKey) {
          case "name":
            content = item.name || "";
            break;
          case "type":
            content = getElementTypeLabel(item.element_type);
            break;
          case "group":
            content = item.group_name || "";
            break;
          case "values":
            content = elementCountText;
            break;
          case "folder":
            content = getFolderPath(item.game_id, 'variables', item.folder) || "";
            break;
          case "description":
            content = item.description || "";
            break;
          case "lastupdate":
            content = formatRelativeTime(item.updated_at);
            break;
          default:
            content = "";
        }

        return {
          key: columnKey,
          fixedX: index === 0,
          content,
        };
      }),
    };
  });

  return {
    items,
    columns: TABLE_COLUMNS,
    fieldMetadata: fields,
  };
}

// Export the module
export const variablesDataSource: DataSourceModule = {
  type: 'objects', // Using 'objects' as the base type
  config,
  fields,
  columns,
  fetchData,
  transformers: {
    toList,
    toTable,
    toCard,
    toTableItems
  }
};

// Export table columns for use in components
export { TABLE_COLUMNS as VARIABLES_TABLE_COLUMNS };
